import { DestroyRef, Injectable, inject } from '@angular/core';
import {
  catchError,
  defer,
  first,
  fromEvent,
  Observable,
  of,
  Subscriber,
  tap,
  throwError,
} from 'rxjs';
import { scriptStore } from './config/script-store.config';
import { SCRIPT_TYPE } from './enum/script-type.enum';
import { IScript, IScriptStatus } from './interface/script.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class DynamicScriptLoaderService {
  private readonly destroyRef = inject(DestroyRef);
  private _scripts: Record<string, IScript> = {};

  constructor() {
    scriptStore.forEach((script: IScript) => {
      this._scripts[script.name] = script;
    });
  }

  /**
   * Dynamically loads a script by its name.
   * @param name - The name of the script to load.
   * @returns An observable that emits the script status.
   */
  loadScript(name: string): Observable<IScriptStatus> {
    // Defer the observable creation until subscription.
    return defer(() => {
      const scriptToLoad = this._scripts[name];

      if (!scriptToLoad) {
        return throwError(() => ({
          name,
          loaded: false,
          status: `[SCRIPT 📄] Script ${name} not found`,
        }));
      }

      if (scriptToLoad.loaded) {
        return of({
          name,
          loaded: true,
          status: `[SCRIPT 📄] Script ${name} already loaded`,
        });
      }

      const scriptElement = document.createElement('script');

      return new Observable<IScriptStatus>((subscriber) => {
        if (scriptToLoad.type === SCRIPT_TYPE.file) {
          scriptElement.type = 'text/javascript';
          scriptElement.src = scriptToLoad.src;
          document.head.appendChild(scriptElement);

          fromEvent(scriptElement, 'load')
            .pipe(
              first(),
              takeUntilDestroyed(this.destroyRef),
              tap(() => this.onLoad(scriptToLoad, subscriber))
            )
            .subscribe();

          fromEvent(scriptElement, 'error')
            .pipe(
              first(),
              tap((errorEvent) => {
                this.onError(scriptToLoad, subscriber, errorEvent);
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
        } else if (scriptToLoad.type === SCRIPT_TYPE.inner) {
          try {
            scriptElement.innerHTML = scriptToLoad.src;
            document.head.appendChild(scriptElement);
            this.onLoad(scriptToLoad, subscriber);
          } catch (error) {
            this.onError(scriptToLoad, subscriber, error);
          }
        }
      }).pipe(catchError((error) => throwError(() => error)));
    });
  }

  /**
   * Handles the successful loading of a script.
   * @param scriptToLoad - The script object.
   * @param subscriber - The subscriber to emit the status.
   */
  private onLoad(
    scriptToLoad: IScript,
    subscriber: Subscriber<IScriptStatus>
  ): void {
    scriptToLoad.loaded = true;
    subscriber.next({
      name: scriptToLoad.name,
      loaded: true,
      status: `[SCRIPT 📄] Script ${scriptToLoad.name} Loaded`,
    });
    subscriber.complete();
  }

  /**
   * Handles errors that occur during script loading.
   * @param scriptToLoad - The script object.
   * @param subscriber - The subscriber to emit the error.
   * @param error - The error encountered.
   */
  private onError(
    scriptToLoad: IScript,
    subscriber: Subscriber<IScriptStatus>,
    error: string | Event | unknown
  ): void {
    scriptToLoad.loaded = false;
    subscriber.error({
      name: scriptToLoad.name,
      loaded: scriptToLoad.loaded,
      status: `[SCRIPT 📄] Script ${scriptToLoad.name} couldn't be loaded due ${error}`,
    });
  }
}
